/**
 * Database Adapter - Unified SQLite/Web support
 * 
 * Provides a unified interface for database operations across:
 * - Native (iOS/Android): Uses expo-sqlite via DatabaseSingleton
 * - Web: Uses localStorage for simple persistence
 */

// Platform-specific imports
let Platform: any
let DatabaseSingleton: any

try {
	Platform = require('react-native').Platform
} catch {
	Platform = { OS: 'web' }
}

// Only import DatabaseSingleton on native platforms
if (Platform.OS !== 'web') {
	try {
		DatabaseSingleton = require('../../database-sqlite/DatabaseSingleton').default
	} catch (e) {
		console.warn('[DatabaseAdapter] Could not load DatabaseSingleton:', e)
	}
}

// ============================================================================
// NATIVE IMPLEMENTATION (iOS/Android)
// ============================================================================

class DatabaseAdapterNative {
	private dbName: string
	private db: any
	private isInitialized: boolean

	constructor(dbName = 'myDB') {
		this.dbName = dbName
		this.db = null
		this.isInitialized = false
	}

	async initialize() {
		if (this.isInitialized) return this.db
		
		// ✅ Use DatabaseSingleton.getRealDatabase() - maneja getInstance() + initializeTables() automáticamente
		const { getRealDatabase } = await import('../../database-sqlite/DatabaseSingleton')
		this.db = await getRealDatabase()
		
		this.isInitialized = true
		return this.db
	}

	async execAsync(sql: string, params: any[] = []) {
		if (!this.isInitialized) await this.initialize()
		return this.db.runSync(sql, params)
	}

	async getAllAsync(sql: string, params: any[] = []) {
		if (!this.isInitialized) await this.initialize()
		return this.db.getAllSync(sql, params)
	}

	async getFirstAsync(sql: string, params: any[] = []) {
		const r = await this.getAllAsync(sql, params)
		return r?.[0] || null
	}

	async transactionAsync(statements: { sql: string; params?: any[] }[]) {
		if (!this.isInitialized) await this.initialize()
		this.db.withTransactionSync(() => {
			for (const { sql, params } of statements) {
				this.db.runSync(sql, params || [])
			}
		})
		return true
	}

	prepareSync(sql: string) {
		if (!this.isInitialized) throw new Error('Database not initialized. Call initialize() first.')
		return {
			getFirstSync: (params: any[] = []) => {
				if (this.db?.getFirstSync) return this.db.getFirstSync(sql, params)
				const rows = this.getAllSync(sql, params)
				return rows?.[0] ?? null
			},
			getAllSync: (params: any[] = []) => {
				if (this.db?.getAllSync) return this.db.getAllSync(sql, params)
				return []
			},
			executeSync: (params: any[] = []) => {
				if (this.db?.runSync) return this.db.runSync(sql, params)
				return { changes: 0, insertId: null }
			},
			finalizeSync: () => {},
		}
	}

	runSync(sql: string, params: any[] = []) {
		if (!this.isInitialized) throw new Error('Database not initialized. Call initialize() first.')
		if (!this.db?.runSync) throw new Error('runSync not available on native db instance')
		return this.db.runSync(sql, params)
	}

	getAllSync(sql: string, params: any[] = []) {
		if (!this.isInitialized) throw new Error('Database not initialized. Call initialize() first.')
		if (!this.db?.getAllSync) throw new Error('getAllSync not available on native db instance')
		return this.db.getAllSync(sql, params)
	}

	getFirstSync(sql: string, params: any[] = []) {
		if (!this.isInitialized) throw new Error('Database not initialized. Call initialize() first.')
		if (this.db?.getFirstSync) return this.db.getFirstSync(sql, params)
		const rows = this.getAllSync(sql, params)
		return rows?.[0] ?? null
	}

	isReady() { return this.isInitialized && !!this.db }
	async close() { 
		try { this.db?.closeSync?.() } catch {} 
		finally { this.db = null; this.isInitialized = false }
	}
}

// ============================================================================
// WEB IMPLEMENTATION (Browser)
// ============================================================================

interface RunResult {
	insertId?: number
	rowsAffected?: number
	rows?: any[]
}

class DatabaseAdapterWeb {
	private dbName: string
	private isInitialized: boolean
	private tables: Map<string, Map<string, any>>

	constructor(dbName = 'myDB') {
		this.dbName = dbName
		this.isInitialized = false
		this.tables = new Map()
	}

	async initialize() {
		if (this.isInitialized) return this
		// Load snapshot from localStorage
		try {
			const raw = globalThis?.localStorage?.getItem(`${this.dbName}_snapshot`)
			if (raw) {
				try {
					const parsed = JSON.parse(raw)
					Object.entries(parsed).forEach(([table, rows]) => {
						const map = new Map<string, any>()
						Object.entries(rows as Record<string, any>).forEach(([k, v]) => map.set(k, v))
						this.tables.set(table, map)
					})
				} catch (e) {
					console.warn('[DatabaseAdapter.web] invalid snapshot JSON, clearing:', (e as any)?.message)
					try { globalThis?.localStorage?.removeItem(`${this.dbName}_snapshot`) } catch {}
				}
			}
		} catch {}
		this.isInitialized = true
		return this
	}

	private persist() {
		try {
			const obj: Record<string, Record<string, any>> = {}
			this.tables.forEach((map, table) => {
				obj[table] = {}
				map.forEach((v, k) => { obj[table][k] = v })
			})
			globalThis?.localStorage?.setItem(`${this.dbName}_snapshot`, JSON.stringify(obj))
		} catch {}
	}

	async execAsync(sql: string, _params: any[] = []): Promise<RunResult> {
		if (!this.isInitialized) await this.initialize()
		const normalized = sql.trim().toUpperCase()
		if (normalized.startsWith('CREATE TABLE IF NOT EXISTS')) {
			const match = /CREATE TABLE IF NOT EXISTS (\w+)/i.exec(sql)
			const tableName = match?.[1]
			if (tableName && !this.tables.has(tableName)) {
				this.tables.set(tableName, new Map())
				this.persist()
			}
			return { rowsAffected: 0 }
		}
		if (normalized.startsWith('INSERT')) {
			const tableMatch = /INTO (\w+)/i.exec(sql)
			const tableName = tableMatch?.[1]
			if (tableName) {
				if (!this.tables.has(tableName)) this.tables.set(tableName, new Map())
				const table = this.tables.get(tableName)!
				const idParam = _params?.[0] ?? `${Date.now()}-${Math.random()}`
				table.set(String(idParam), { _id: idParam })
				this.persist()
				return { insertId: 0, rowsAffected: 1 }
			}
			return { rowsAffected: 0 }
		}
		return { rowsAffected: 0 }
	}

	async getAllAsync(sql: string, params: any[] = []): Promise<any[]> {
		if (!this.isInitialized) await this.initialize()
		const upper = sql.trim().toUpperCase()
		if (upper.startsWith('SELECT')) {
			const tableMatch = /FROM (\w+)/i.exec(sql)
			const tableName = tableMatch?.[1]
			if (tableName && this.tables.has(tableName)) {
				return Array.from(this.tables.get(tableName)!.values())
			}
			return []
		}
		return []
	}

	async getFirstAsync(sql: string, params: any[] = []) {
		const all = await this.getAllAsync(sql, params)
		return all[0] || null
	}

	async transactionAsync(statements: { sql: string; params?: any[] }[]) {
		for (const s of statements) { await this.execAsync(s.sql, s.params || []) }
		return true
	}

	transaction(arg: any, mode?: string) {
		// IndexedDB-style format
		if (Array.isArray(arg) || typeof arg === 'string') {
			const storeNames: string[] = Array.isArray(arg) ? arg : [arg]
			const tx: any = {
				objectStore: (name: string) => {
					if (!this.tables.has(name)) { this.tables.set(name, new Map()) }
					const table = this.tables.get(name)!

					const makeRequest = (executor: () => any, transform?: (res: any) => any) => {
						const request: any = { result: undefined, error: undefined, onsuccess: undefined as any, onerror: undefined as any }
						queueMicrotask(() => {
							try {
								const raw = executor()
								request.result = transform ? transform(raw) : raw
								request.onsuccess && request.onsuccess({ target: request })
							} catch (e) {
								request.error = e
								if (request.onerror) request.onerror({ target: request })
							}
						})
						return request
					}

					return {
						add: (value: any) => makeRequest(() => { const id = value._id || value.id || `${Date.now()}-${Math.random()}`; table.set(String(id), { ...value, _id: id }); this.persist(); }),
						put: (value: any) => makeRequest(() => { const id = value._id || value.id || `${Date.now()}-${Math.random()}`; table.set(String(id), { ...value, _id: id }); this.persist(); }),
						get: (key: string) => makeRequest(() => table.get(String(key))),
						getAll: () => makeRequest(() => Array.from(table.values())),
						clear: () => makeRequest(() => { table.clear(); this.persist(); }),
					}
				},
			}
			return tx
		}

		// SQLite callback style
		const callback = arg
		if (typeof callback !== 'function') { console.error('[WebDB] transaction callback no es función'); return }
		const tx = {
			executeSql: async (sql: string, params: any[] = [], successCb?: Function, errorCb?: Function) => {
				try {
					const result = await this.execAsync(sql, params)
					let rowsArray: any[] = []
					try { if (/^\s*SELECT/i.test(sql)) { rowsArray = await this.getAllAsync(sql, params) } } catch {}
					const rows = { _array: rowsArray, length: rowsArray.length, item: (i: number) => rowsArray[i] }
					successCb && successCb(tx, { rows, insertId: result.insertId ?? null, rowsAffected: result.rowsAffected ?? 0 })
				} catch (e) {
					console.error('[WebDB] executeSql error', e)
					errorCb && errorCb(tx, e)
				}
			},
		}
		try { callback(tx) } catch (e) { console.error('[WebDB] transaction callback error', e) }
	}

	prepareSync(sql: string) {
		return {
			getAllSync: () => [],
			getFirstSync: () => null,
			executeSync: () => ({ changes: 0, insertId: null }),
			finalizeSync: () => {},
		}
	}

	isReady() { return this.isInitialized }
	async close() { this.isInitialized = false }
}

// ============================================================================
// EXPORTS - Platform-specific singleton
// ============================================================================

type DatabaseAdapterType = DatabaseAdapterNative | DatabaseAdapterWeb

let instance: DatabaseAdapterType | null = null

export const getDatabaseAdapter = (dbName = 'myDB'): DatabaseAdapterType => {
	if (!instance) {
		if (Platform.OS === 'web') {
			instance = new DatabaseAdapterWeb(dbName)
		} else {
			instance = new DatabaseAdapterNative(dbName)
		}
	}
	return instance
}

export const initializeDatabaseAdapter = async (dbName = 'myDB'): Promise<DatabaseAdapterType> => {
	const adapter = getDatabaseAdapter(dbName)
	await adapter.initialize()
	return adapter
}

export default Platform.OS === 'web' ? DatabaseAdapterWeb : DatabaseAdapterNative
